import { createSlice, createSelector } from '@reduxjs/toolkit';
import { RootState, SpaceType } from '../../store';
import { spacesSelector } from './spaces';

export const editSlice = createSlice({
  name: 'edit',
  initialState: {
    isEditing: false,
    isEditingSettings: false,
    editedSpaceId: null as string | null,
    editedSpacePosition: null as { lat: number, lng: number, layerId: number } | null,
    // Overlay the edited new space will be shown as, when created from an overlay
    editedSpaceOverlay: null as { id: string; name: string } | null,
    editedLabelId: null as string | null,
    selectedLabelIds: [] as string[],
    // Overlay edited in the overlay editor, and all the selected ones (it included)
    selectedOverlayId: null as string | null,
    selectedOverlayIds: [] as string[],
    markerEditingSpaceId: null as string | null,
    livePreview: false,
    mode: '' as '' | 'pin' | 'label' | 'overlay' | 'marker' | 'drawingPath',
    externalFontFaceDeclarations: {} as Record<string, string>, // Map from font family name to text of @font-face rule
  },
  reducers: {
    toggleEdit: (state) => {
      state.isEditing = !state.isEditing;
      if (!state.isEditing) {
        state.isEditingSettings = false;
        state.editedSpaceId = null;
        state.editedSpaceOverlay = null;
        state.editedLabelId = null;
        state.selectedLabelIds = [];
        state.selectedOverlayId = null;
        state.selectedOverlayIds = [];
        state.markerEditingSpaceId = null;
      }
    },
    toggleEditingSettings: (state) => ({
      ...state,
      isEditingSettings: !state.isEditingSettings,
    }),
    setEditedSpace: (state, { payload }) => {
      state.editedSpaceId = payload?.id ?? null;
      state.editedSpacePosition = payload?.position ?? null;
      state.editedSpaceOverlay = payload?.overlay ?? null;
    },
    setEditedSpaceId: (state, { payload }) => {
      state.editedSpaceId = payload;
      if (!payload) state.editedSpaceOverlay = null;
    },
    setEditedSpacePosition: (state, { payload }) => {
      state.editedSpacePosition = payload;
    },
    setEditedLabelId: (state, { payload }) => {
      state.editedLabelId = payload;
    },
    setSelectedLabelIds: (state, { payload }: { payload: string[] }) => {
      state.selectedLabelIds = payload;
    },
    // Selects a single overlay, or none
    setSelectedOverlayId: (state, { payload }: { payload: string | null }) => {
      state.selectedOverlayId = payload;
      state.selectedOverlayIds = payload ? [payload] : [];
    },
    // Selects several overlays, the first one being edited
    setSelectedOverlayIds: (state, { payload }: { payload: string[] }) => {
      state.selectedOverlayId = payload[0] ?? null;
      state.selectedOverlayIds = payload;
    },
    // Adds an overlay to the selection, or removes it. The edited one stays,
    // unless removed
    toggleSelectedOverlayId: (state, { payload }: { payload: string }) => {
      if (state.selectedOverlayIds.includes(payload)) {
        state.selectedOverlayIds = state.selectedOverlayIds.filter((id) => id !== payload);
      } else {
        state.selectedOverlayIds.push(payload);
      }
      if (!state.selectedOverlayId || !state.selectedOverlayIds.includes(state.selectedOverlayId)) {
        state.selectedOverlayId = state.selectedOverlayIds[0] ?? null;
      }
    },
    setLivePreview: (state, { payload }) => {
      state.livePreview = payload;
    },
    setEditMode: (state, { payload }) => {
      state.mode = payload;
      // reset non-saved labels/pins on editMode change
      state.editedSpaceId = null;
      state.editedSpacePosition = null;
      state.editedSpaceOverlay = null;
      state.editedLabelId = null;
      state.selectedLabelIds = [];
      state.selectedOverlayId = null;
      state.selectedOverlayIds = [];
      state.markerEditingSpaceId = null;
    },
    setMarkerEditingSpaceId: (state, { payload }) => {
      state.markerEditingSpaceId = payload;
    },
    registerExternalFontFaceDeclarations: (state, { payload }) => {
      state.externalFontFaceDeclarations = { ...state.externalFontFaceDeclarations, ...payload };
    },
  },
});

export const isEditingSelector = (state: RootState) => state.edit.isEditing;
export const isEditingSettingsSelector = (state: RootState) => state.edit.isEditingSettings;
export const editModeSelector = (state: RootState) => state.edit.mode;
export const externalFontFaceDeclarationsSelector = (state: RootState) => state.edit.externalFontFaceDeclarations;

export const editedSpaceIdSelector = (state: RootState) => state.edit.editedSpaceId;
export const editedSpacePositionSelector = (state: RootState) => state.edit.editedSpacePosition;
export const editedSpaceOverlaySelector = (state: RootState) => state.edit.editedSpaceOverlay;
export const markerEditingSpaceIdSelector = (state: RootState) => state.edit.markerEditingSpaceId;
export const livePreviewSelector = (state: RootState) => state.edit.livePreview;

export const selectedLabelIdsSelector = (state: RootState) => state.edit.selectedLabelIds;
export const selectedOverlayIdSelector = (state: RootState) => state.edit.selectedOverlayId;
export const selectedOverlayIdsSelector = (state: RootState) => state.edit.selectedOverlayIds;

export const editedSpaceSelector = createSelector(
  editedSpaceIdSelector,
  spacesSelector,
  (editedSpaceId, spaces): SpaceType | null => {
    if (!editedSpaceId) return null;
    return spaces.find((s) => s.id === editedSpaceId) || ({ id: editedSpaceId } as SpaceType);
  },
);

export const markerEditingSpaceSelector = createSelector(
  markerEditingSpaceIdSelector,
  spacesSelector,
  (markerEditingSpaceId, spaces): SpaceType | null => {
    if (!markerEditingSpaceId) return null;
    return spaces.find((s) => s.id === markerEditingSpaceId) || ({ id: markerEditingSpaceId } as SpaceType);
  },
);

export const {
  toggleEdit,
  toggleEditingSettings,
  setEditedSpaceId,
  setEditedSpacePosition,
  setEditedLabelId,
  setSelectedLabelIds,
  setSelectedOverlayId,
  setSelectedOverlayIds,
  toggleSelectedOverlayId,
  setLivePreview,
  setEditedSpace,
  setEditMode,
  setMarkerEditingSpaceId,
  registerExternalFontFaceDeclarations,
} = editSlice.actions;

export default editSlice.reducer;
